import { LitElement, html } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import "ui-components/modal/wow-modal";

import { styles } from "./custom-color-picker.css";

import { t } from "../../translations/i18n";
import "../advanced-color-picker/advanced-color-picker";

interface CustomColors {
  primary: string;
  secondary: string;
}

@customElement("custom-color-picker")
export class CustomColorPicker extends LitElement {
  static override styles = styles;

  @property({ type: Object })
  initialColors?: CustomColors;

  @state()
  private primaryColor = "#FF5733";

  @state()
  private secondaryColor = "#33C1FF";

  @state()
  private hasValidationErrors = false;

  @state()
  private errorMessage = "";

  override connectedCallback() {
    super.connectedCallback();

    // Initialize with provided colors or defaults
    if (this.initialColors) {
      this.primaryColor = this.initialColors.primary;
      this.secondaryColor = this.initialColors.secondary;
    }
  }

  override willUpdate(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has("initialColors") && this.initialColors) {
      this.primaryColor = this.initialColors.primary;
      this.secondaryColor = this.initialColors.secondary;
    }
  }

  private handlePrimaryColorChange(event: CustomEvent) {
    const { hex } = event.detail;
    this.primaryColor = hex;
    this.clearValidationErrors();
  }

  private handleSecondaryColorChange(event: CustomEvent) {
    const { hex } = event.detail;
    this.secondaryColor = hex;
    this.clearValidationErrors();
  }

  private clearValidationErrors() {
    this.hasValidationErrors = false;
    this.errorMessage = "";
  }

  private validateColors(): boolean {
    const hexPattern = /^#[0-9A-Fa-f]{6}$/;

    if (!hexPattern.test(this.primaryColor)) {
      this.hasValidationErrors = true;
      this.errorMessage = "Primary color must be a valid hex color (e.g., #FF5733)";
      return false;
    }

    if (!hexPattern.test(this.secondaryColor)) {
      this.hasValidationErrors = true;
      this.errorMessage = "Secondary color must be a valid hex color (e.g., #33C1FF)";
      return false;
    }

    // Check if colors are too similar
    if (this.primaryColor.toLowerCase() === this.secondaryColor.toLowerCase()) {
      this.hasValidationErrors = true;
      this.errorMessage = "Primary and secondary colors should be different";
      return false;
    }

    return true;
  }

  private handleSave() {
    if (this.validateColors()) {
      this.dispatchEvent(
        new CustomEvent("save", {
          detail: {
            colors: {
              primary: this.primaryColor,
              secondary: this.secondaryColor,
            },
          },
          bubbles: true,
        }),
      );
    }
  }

  private handleCancel() {
    this.dispatchEvent(
      new CustomEvent("cancel", {
        bubbles: true,
      }),
    );
  }

  private handleModalClose() {
    // Modal close button clicked - same as cancel
    this.handleCancel();
  }

  protected override render() {
    return html`
      <wow-modal
        mobile-fullscreen
        variant="responsive"
        @close=${this.handleModalClose}
        @footer-button-clicked=${this.handleSave}
      >
        <span slot="header">${t("profile.chooseYourOwnColors")}</span>

        <div class="custom-color-picker-content">
          <!-- Column 1: Primary Color Picker -->
          <div class="color-column-card">
            <advanced-color-picker
              .value=${this.primaryColor}
              .label=${t("profile.primaryColor")}
              @color-change=${this.handlePrimaryColorChange}
            ></advanced-color-picker>
          </div>

          <!-- Column 2: Secondary Color Picker -->
          <div class="color-column-card">
            <advanced-color-picker
              .value=${this.secondaryColor}
              .label=${t("profile.secondaryColor")}
              @color-change=${this.handleSecondaryColorChange}
            ></advanced-color-picker>
          </div>

          <!-- Column 3: Preview -->
          <div class="preview-column-card">
            <p class="preview-text">${t("profile.thisIsPreview")}</p>
            <div class="preview-rectangle" style="background: ${this.primaryColor}; color: ${this.secondaryColor};">
              ${t("profile.buttonAppearance")}
            </div>
          </div>
        </div>

        ${this.hasValidationErrors ? html`<div class="validation-error">${this.errorMessage}</div>` : ""}

        <span slot="footer-button-text">${t("profile.saveColors")}</span>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "custom-color-picker": CustomColorPicker;
  }
}
